/**
 * One navigation link (sidebar or mobile drawer); collapsed mode shows the
 * icon with a tooltip. Whether it is the current page is decided once for
 * the whole menu (the most specific match), so two items never light up.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { NavItem } from './nav-config';

interface NavItemLinkProps {
  item: NavItem;
  active?: boolean;
  collapsed?: boolean;
  onNavigate?: () => void;
}

export function NavItemLink({ item, active = false, collapsed = false, onNavigate }: NavItemLinkProps) {
  const { t } = useTranslation('nav');
  const label = t(item.labelKey);
  const link = (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center rounded-xl py-2 text-sm transition-colors',
        collapsed ? 'justify-center px-0' : 'gap-3 px-3',
        active ? 'bg-primary font-medium text-primary-foreground shadow-sm shadow-primary/25' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <item.icon className={cn('h-4 w-4 flex-shrink-0', collapsed && 'h-5 w-5')} />
      {collapsed ? <span className="sr-only">{label}</span> : label}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip content={label} side="right" className="block w-full">
      {link}
    </Tooltip>
  );
}
